/**
 * 参考线（DESIGN.md §6.5 补充：「视图→新建参考线…/清除参考线/锁定参考线」，
 * 参照 Photoshop）。全部落地走同一条 `createSetGuidesCommand`（替换整份
 * 列表），跟画布尺寸同一种"结构命令只管落地，语义留给调用方"的分工。
 */
import { createSetGuidesCommand } from './commands/canvas-commands';
import type { Guide } from '../document/types';
import type { AppStore } from './store';

/** 新建一条参考线。位置会被夹到画布范围内。返回是否执行（无文档时 false）。 */
export function addGuide(
  store: AppStore,
  axis: 'horizontal' | 'vertical',
  position: number,
  locked: boolean,
): boolean {
  const document = store.document;
  if (document === null) return false;
  const max = axis === 'horizontal' ? document.size.height : document.size.width;
  const clamped = Math.max(0, Math.min(max, Math.round(position)));
  const guide: Guide = { axis, position: clamped, locked };
  store.dispatch(createSetGuidesCommand([...document.guides, guide]));
  return true;
}

/** 移动一条参考线（拖拽用）。已锁定的参考线拒绝移动。 */
export function moveGuide(store: AppStore, index: number, position: number): boolean {
  const document = store.document;
  if (document === null) return false;
  const guide = document.guides[index];
  if (guide === undefined || guide.locked) return false;
  const max = guide.axis === 'horizontal' ? document.size.height : document.size.width;
  const clamped = Math.max(0, Math.min(max, Math.round(position)));
  const next = document.guides.map((g, i) => (i === index ? { ...g, position: clamped } : g));
  store.dispatch(createSetGuidesCommand(next));
  return true;
}

/** 删除一条参考线（拖回标尺/按 Delete）。已锁定的参考线拒绝删除。 */
export function removeGuideAt(store: AppStore, index: number): boolean {
  const document = store.document;
  if (document === null) return false;
  const guide = document.guides[index];
  if (guide === undefined || guide.locked) return false;
  store.dispatch(createSetGuidesCommand(document.guides.filter((_, i) => i !== index)));
  return true;
}

/** 清除全部参考线（忽略锁定——"清除参考线"这个命令本身就是显式的整体操作）。 */
export function clearGuides(store: AppStore): boolean {
  const document = store.document;
  if (document === null || document.guides.length === 0) return false;
  store.dispatch(createSetGuidesCommand([]));
  return true;
}

/** 切换全部参考线的锁定状态（真实 PS「锁定参考线」是全局开关，不是逐条锁）。 */
export function setGuidesLocked(store: AppStore, locked: boolean): boolean {
  const document = store.document;
  if (document === null || document.guides.length === 0) return false;
  store.dispatch(createSetGuidesCommand(document.guides.map((g) => ({ ...g, locked }))));
  return true;
}
